import { getMenuItems } from './getMenuItems';
import { initializeView } from './initializeView';
import { updateSelectedViewDate } from './updateSelectedViewDate';
import { BaseCalendarScenarioId } from '../constants';
import { CalendarPayload, CalendarViewType, UserEvent } from '../schema';
import { getNumDaysWeekViews, isDayGridViewType } from '../utils/calendarViewTypeUtils';
import { getStartOfWeek, getWorkDays, convertToOwaDate } from '../utils/dateUtils';
import { updateCalendarIds } from 'owa-agenda-events-list';
import {
    getAccountFromCalendarId,
    removeCalendarWithIDFromCalendarsCache,
} from 'owa-calendar-cache';
import { calendarCacheInitializedForAccount } from 'owa-calendar-cache-loader';
import { OwaDate, asDate } from 'owa-datetime';
import { observableTodayChanged, observableToday } from 'owa-observable-datetime';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import { useAssetCommands, useAssetId, usePatchPartialPayload } from 'owa-timestream-asset-sdk';
import { navigateCalendarAssetToDate, setAssetsLock } from 'owa-timestream-asset-sdk-actions';
import { CalendarAssetCommandDescriptors } from 'owa-timestream-asset-sdk-calendarevents';
import { getFolderIdByCalendarID, initializeCalendars } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';
import type { PerformanceDatapoint } from 'owa-analytics';
import { convertIdsToTargetFormat, ConvertIdSource } from 'owa-immutable-id';
import { isConversionNeeded, getTargetFormat } from 'owa-immutable-id-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isConnectedAccount, getDefaultConnectedAccount } from 'owa-accounts-store';
import {
    allCalendarAccountsAndCacheLoaded,
    getIsAllCalendarAccountsAndCacheLoaded,
    removedConnectedAccountIdCleanup,
} from 'owa-calendar-account-store';
import {
    getAccountActiveCalendarIds,
    partitionIds,
    isAcccountFullyLoaded,
} from '../utils/selectedCalendarUtils';

export default function useCalendarViewState(
    { payload }: AssetCommonViewProps<CalendarPayload>,
    assetPerfDatapoint: React.RefObject<PerformanceDatapoint | undefined>
) {
    const {
        calendarIds,
        viewType,
        connectedAccountCalendarIds = [],
        numDaysInDayView,
        title,
    } = payload;

    const combinedCalendarIds = calendarIds.concat(connectedAccountCalendarIds);

    const scenarioId = BaseCalendarScenarioId + '-' + useAssetId();

    const patchPartialPayload = usePatchPartialPayload<CalendarPayload>();

    const today = observableToday();
    const [selectedDate, setSelectedDate] = React.useState<Date>(asDate(today));
    const owaSelectedDate = convertToOwaDate(selectedDate);

    const defaultUserId = getUserMailboxInfo().userIdentity;

    const formatCalendarIds = React.useCallback(
        async (inputCalendarIds: string[], userIdentity: string) => {
            if (!userIdentity) {
                if (connectedAccountCalendarIds.length > 0) {
                    patchPartialPayload({ connectedAccountCalendarIds: [] });
                    removedConnectedAccountIdCleanup();
                }
                return;
            }
            const accountEndpointIdsMap = partitionIds(inputCalendarIds);
            if (!isAcccountFullyLoaded(userIdentity, accountEndpointIdsMap)) {
                /** in this case, we should not try to format or initialize this account's calendar ids. We should either:
                 * (1) wait for the calendarCacheInitializedAction to fire for the endpoint that is not yet loaded yet,
                 * in which case this formatCalendarIds will fire again for this userId
                 * (2) VSO 84836: add better error handling for an account's endpoint that did not load
                 */
                return;
            }
            const activeAccountCalendarIds = getAccountActiveCalendarIds(
                inputCalendarIds,
                userIdentity
            );
            let formattedCalendarIds: string[] = [];
            if (isConversionNeeded(inputCalendarIds, userIdentity)) {
                formattedCalendarIds = await convertIdsToTargetFormat(
                    activeAccountCalendarIds,
                    getTargetFormat(userIdentity),
                    userIdentity,
                    ConvertIdSource.BoardCalendarAsset
                );
            } else {
                formattedCalendarIds = activeAccountCalendarIds;
            }
            const isConnected = isConnectedAccount(userIdentity);

            initializeCalendars();
            initializeView(
                scenarioId,
                viewType,
                owaSelectedDate,
                isConnected ? calendarIds.concat(formattedCalendarIds) : formattedCalendarIds,
                numDaysInDayView,
                assetPerfDatapoint
            );
            if (isConnected) {
                patchPartialPayload({ connectedAccountCalendarIds: formattedCalendarIds });
            } else {
                patchPartialPayload({ calendarIds: formattedCalendarIds });
            }
        },
        [
            scenarioId,
            viewType,
            owaSelectedDate,
            numDaysInDayView,
            assetPerfDatapoint,
            patchPartialPayload,
            combinedCalendarIds,
        ]
    );

    const updateSelectedCalendars = React.useCallback(
        (selectedCalendarIds: string[], isConnectedAccount: boolean) => {
            let updatedCombinedCalendarIds: string[] = [];
            if (isConnectedAccount) {
                patchPartialPayload({ connectedAccountCalendarIds: selectedCalendarIds });
                calendarIds.forEach(calendarId => {
                    updatedCombinedCalendarIds.push(calendarId);
                });
            } else {
                patchPartialPayload({ calendarIds: selectedCalendarIds });
                connectedAccountCalendarIds.forEach(calendarId => {
                    updatedCombinedCalendarIds.push(calendarId);
                });
            }
            selectedCalendarIds.forEach(calendarId => {
                updatedCombinedCalendarIds.push(calendarId);
            });
            updateCalendarIds(updatedCombinedCalendarIds, scenarioId);

            // We need to force re-render day grid views when calendar selection changes
            // since the component has not been refactored/fixed yet to properly auto-rerender as needed
            if (isDayGridViewType(viewType)) {
                initializeView(
                    scenarioId,
                    viewType,
                    owaSelectedDate,
                    updatedCombinedCalendarIds,
                    numDaysInDayView
                );
            }
        },
        [
            scenarioId,
            viewType,
            owaSelectedDate,
            numDaysInDayView,
            patchPartialPayload,
            combinedCalendarIds,
        ]
    );

    React.useEffect(() => {
        const hasSelectedCalendars = combinedCalendarIds.length === 0;
        assetPerfDatapoint.current?.addCustomProperty('viewType', viewType);
        assetPerfDatapoint.current?.addCustomProperty('hasSelectedCalendars', hasSelectedCalendars);
        // primary account calendar init
        formatCalendarIds(calendarIds, defaultUserId);

        // only format/remove connected account calendar ids if calendar cache has finished loading, else
        // wait for calendarCacheInitializedForAccount/allCalendarAccountsAndCacheLoaded orchestrators
        if (getIsAllCalendarAccountsAndCacheLoaded()) {
            const connectedAccount = getDefaultConnectedAccount();
            formatCalendarIds(connectedAccountCalendarIds, connectedAccount?.userIdentity);
        }
    }, []);

    // We need to set up an effect to force-rerender day grid views when certain values and settings change,
    // since the component has not been refactored/fixed yet to properly auto-rerender as needed
    // For workweek view type, we need to completely re-init to correctly update surface to reflect workweek settings
    const workDays = getWorkDays();
    // NOTE: startOfWeek effect dep is a date-time string in order for deps comparison to not over-trigger
    const startOfWeek = getStartOfWeek(convertToOwaDate(selectedDate)).toString();
    React.useEffect(() => {
        if (isDayGridViewType(viewType)) {
            initializeView(
                scenarioId,
                viewType,
                owaSelectedDate,
                combinedCalendarIds,
                numDaysInDayView
            );
        }
    }, [workDays, startOfWeek, today]);

    // This effect is used to make sure that calendar widgets are in sync across browser windows.
    React.useEffect(() => {
        initializeView(
            scenarioId,
            viewType,
            owaSelectedDate,
            combinedCalendarIds,
            numDaysInDayView
        );
    }, [calendarIds, connectedAccountCalendarIds]);

    // We need to set up an orchestrator to force-rerender day grid views when cache is initialized for an account,
    // since the component has not been refactored/fixed yet to properly auto-rerender as needed
    // This will only run if the account is initialized after load into board view
    useOrchestrator(
        calendarCacheInitializedForAccount,
        actionMessage => {
            const { userId } = actionMessage;
            const isPrimaryId = userId == defaultUserId;
            formatCalendarIds(isPrimaryId ? calendarIds : connectedAccountCalendarIds, userId);
        },
        [
            scenarioId,
            viewType,
            owaSelectedDate,
            combinedCalendarIds,
            numDaysInDayView,
            formatCalendarIds,
        ]
    );

    // We need to set up an orchestrator to update removed connected account ids or accounts all together
    // once the full owa account store is initialized and checked for server connected accounts
    useOrchestrator(
        allCalendarAccountsAndCacheLoaded,
        actionMessage => {
            const { isSuccess, userIdentity } = actionMessage;
            // only format if userIdentity is undefined and therefore there is no connected account
            if (isSuccess && !userIdentity) {
                formatCalendarIds(connectedAccountCalendarIds, userIdentity);
            }
        },
        [formatCalendarIds, connectedAccountCalendarIds]
    );

    const updateDateForView = React.useCallback(
        (scenarioId: string, viewType: CalendarViewType, date: OwaDate) => {
            setSelectedDate(asDate(date));
            updateSelectedViewDate(scenarioId, viewType, date);
        },
        []
    );

    useOrchestrator(
        observableTodayChanged,
        () => {
            const todayDate = observableToday();
            updateDateForView(scenarioId, viewType, todayDate);
        },
        [scenarioId, viewType, updateDateForView]
    );

    useOrchestrator(
        navigateCalendarAssetToDate,
        actionMessage => {
            updateDateForView(scenarioId, viewType, actionMessage.date);
        },
        [scenarioId, viewType, updateDateForView]
    );

    useOrchestrator(
        removeCalendarWithIDFromCalendarsCache,
        actionMessage => {
            // only remove calendarId from payload if shouldPersistCalendarEntry is true, otherwise flow is just updating folderId
            if (!actionMessage.shouldPersistCalendarEntry) {
                const accountCalendarIds = isConnectedAccount(actionMessage.userId)
                    ? connectedAccountCalendarIds
                    : calendarIds;

                const filteredCalendarIds = accountCalendarIds.filter(
                    calendarId => calendarId != actionMessage.calendarId
                );

                if (filteredCalendarIds.length < accountCalendarIds.length) {
                    updateSelectedCalendars(
                        filteredCalendarIds,
                        isConnectedAccount(actionMessage.userId)
                    );
                }
            }
        },
        [calendarIds, connectedAccountCalendarIds, updateSelectedCalendars]
    );

    const onSelectDate = React.useCallback(
        (date: OwaDate) => {
            updateDateForView(scenarioId, viewType, date);
        },
        [scenarioId, viewType, updateDateForView]
    );

    const onSelectViewType = React.useCallback(
        (selection: CalendarViewType) => {
            if (selection == CalendarViewType.Week || selection == CalendarViewType.WorkWeek) {
                patchPartialPayload({
                    viewType: selection,
                    numDaysInDayView: getNumDaysWeekViews(selection),
                });
            } else {
                patchPartialPayload({ viewType: selection });
            }

            // unlock assets as switching calendars might cause widget resize
            setAssetsLock(false);

            initializeView(
                scenarioId,
                selection,
                owaSelectedDate,
                combinedCalendarIds,
                numDaysInDayView
            );
        },
        [
            scenarioId,
            viewType,
            owaSelectedDate,
            combinedCalendarIds,
            numDaysInDayView,
            patchPartialPayload,
        ]
    );

    const onChangeNumDaysInDayView = React.useCallback(
        (numDaysInDayView: number) => {
            patchPartialPayload({
                viewType: CalendarViewType.Day,
                numDaysInDayView: numDaysInDayView,
            });
            initializeView(
                scenarioId,
                CalendarViewType.Day,
                owaSelectedDate,
                combinedCalendarIds,
                numDaysInDayView
            );
        },
        [scenarioId, owaSelectedDate, calendarIds, connectedAccountCalendarIds, patchPartialPayload]
    );

    useAssetCommands(
        [
            {
                ...CalendarAssetCommandDescriptors.switchCalendarView,
                getMenuItems: () =>
                    getMenuItems(
                        viewType,
                        numDaysInDayView,
                        onSelectViewType,
                        onChangeNumDaysInDayView
                    ),
            },
        ],
        [viewType, onSelectViewType, onChangeNumDaysInDayView, numDaysInDayView]
    );

    const onSelectCalendar = React.useCallback(
        (selection: string) => {
            return (event: UserEvent) => {
                event.preventDefault();
                const calUserIdentity = getAccountFromCalendarId(selection);
                const isConnected = isConnectedAccount(calUserIdentity);
                const ids = !isConnected ? calendarIds : connectedAccountCalendarIds;
                const index = ids.indexOf(selection);
                const calendarIdsCopy = ids.slice();

                if (index < 0) {
                    calendarIdsCopy.push(selection);
                } else {
                    calendarIdsCopy.splice(index, 1);
                }

                updateSelectedCalendars(calendarIdsCopy, isConnected);
            };
        },
        [combinedCalendarIds, updateSelectedCalendars]
    );

    const folderIds = calendarIds.map(
        calendarId => getFolderIdByCalendarID(calendarId) ?? undefined
    );

    const onChangeTitle = React.useCallback(
        (newTitle?: string) => {
            patchPartialPayload({
                title: newTitle,
            });
        },
        [patchPartialPayload]
    );

    return {
        scenarioId,
        combinedCalendarIds,
        folderIds,
        viewType,
        selectedDate,
        isLoaded: combinedCalendarIds.length > 0,
        onSelectCalendar,
        onSelectViewType,
        onSelectDate /** call this when the date changes in a subcomponent, it updates the asset's date to remain in sync with its subcomponents*/,
        onDateUpdated: setSelectedDate /** call this when the date changes in the asset, it updates the asset's date and notifies consumers of changes so they can remain in sync */,
        numDaysInDayView,
        title,
        onChangeTitle,
        assetPerfDatapoint,
    };
}
